import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
} from 'react';
import type { CanvasSummary, VideoReference } from '@/api/types';
import { canvasCountOf } from '@/library/useCanvases';
import { cx } from '@/lib/cx';
import { useTranslator } from '@/i18n/LocaleProvider';
import { useLongPress } from '@/lib/useLongPress';
import { CanvasFlyout } from './CanvasFlyout';
import styles from './VideoGrid.module.css';

/** Шаг слайдшоу кейфреймов под курсором. */
const KEYFRAME_INTERVAL_MS = 500;

export interface VideoCardProps {
  video: VideoReference;
  aspect: string;
  isFirst: boolean;
  selected: boolean;
  saved: boolean;
  hovered: boolean;
  fly: { dir: 'left' | 'right' } | null;
  canvases: CanvasSummary[];
  sizeIsSmall: boolean;
  onHover: (id: number | null) => void;
  onOpen: (video: VideoReference) => void;
  onToggleSelect: (id: number) => void;
  onFlyEnter: (id: number, rect: DOMRect) => void;
  onFlyLeave: (id: number) => void;
  onFlyToggle: (id: number, rect: DOMRect) => void;
  onFlyPick: (video: VideoReference, canvasId: number, alreadyPresent: boolean) => void;
  onFlyNew: (video: VideoReference) => void;
  onLongPress: (video: VideoReference) => void;
}

/** Карточка каталога: только кадр, ленточки статусов, «+» и «Canvas» по ховеру; на мобильном — long-press. */
export const VideoCard = memo(function VideoCard({
  video,
  aspect,
  isFirst,
  selected,
  saved,
  hovered,
  fly,
  canvases,
  sizeIsSmall,
  onHover,
  onOpen,
  onToggleSelect,
  onFlyEnter,
  onFlyLeave,
  onFlyToggle,
  onFlyPick,
  onFlyNew,
  onLongPress,
}: VideoCardProps) {
  const { pressing, handlers, consumeLongPress } = useLongPress(() => onLongPress(video));
  const canvasCount = canvasCountOf(video, canvases);
  const showActs = hovered || selected || !!fly;
  const thumbnail = video.extra?.thumbnail_url ?? video.media?.poster_url ?? '';

  // Кадр проявляется по загрузке; из кеша картинка готова раньше onLoad — читаем `complete`.
  const [loaded, setLoaded] = useState(false);
  const bindImage = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete) setLoaded(true);
  }, []);

  // Слайдшоу кейфреймов под курсором: при первом ховере картинки лениво прогреваются
  // (новый <img> в памяти → кеш браузера), дальше идёт цикл по индексам. Без кейфреймов
  // остаётся статичная обложка.
  const keyframes = video.extra?.keyframes;
  const [frame, setFrame] = useState<number | null>(null);
  const preloaded = useRef<HTMLImageElement[] | null>(null);

  useEffect(() => {
    if (!hovered || !keyframes || keyframes.length === 0) {
      setFrame(null);
      return;
    }
    if (preloaded.current === null) {
      preloaded.current = keyframes.map((src) => {
        const image = new Image();
        image.src = src;
        return image;
      });
    }
    setFrame(0);
    const timer = window.setInterval(
      () => setFrame((prev) => (prev === null ? 0 : (prev + 1) % keyframes.length)),
      KEYFRAME_INTERVAL_MS,
    );
    return () => window.clearInterval(timer);
  }, [hovered, keyframes]);

  const shown = (frame !== null ? (keyframes?.[frame] ?? thumbnail) : thumbnail) || thumbnail;

  const open = () => {
    if (consumeLongPress()) return;
    onOpen(video);
  };

  const { t, tp } = useTranslator();
  const flyToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onFlyToggle(video.id, event.currentTarget.getBoundingClientRect());
  };

  return (
    <article
      className={cx(styles.card, selected && styles.cardSelected, fly && styles.cardFly)}
      style={{ '--card-ar': aspect } as CSSProperties}
      data-tour={isFirst ? 'card' : undefined}
      data-pressing={pressing ? 'true' : 'false'}
      data-video-id={video.id}
      onMouseEnter={() => onHover(video.id)}
      onMouseLeave={() => onHover(null)}
      {...handlers}
    >
      <button type="button" className={styles.media} onClick={open} aria-label={video.title}>
        {thumbnail ? (
          <img
            ref={bindImage}
            src={shown}
            alt=""
            draggable={false}
            loading="lazy"
            data-loaded={loaded ? 'true' : 'false'}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        ) : null}
        {hovered && !fly && !sizeIsSmall ? <div className={styles.shade} /> : null}
        {saved || canvasCount > 0 ? (
          <span className={styles.marks}>
            {saved ? (
              <span className={cx(styles.ribbon, styles.ribbonSaved)} title={t('catalog.saved')} />
            ) : null}
            {canvasCount > 0 ? (
              <span
                className={cx(styles.ribbon, styles.ribbonCanvas)}
                title={tp('catalog.onCanvases', canvasCount)}
              >
                <span />
              </span>
            ) : null}
          </span>
        ) : null}
      </button>

      <div className={cx(styles.acts, styles.actsLeft)} data-show={showActs ? 'true' : 'false'}>
        <button
          type="button"
          className={cx(styles.select, selected && styles.selectOn)}
          title={selected ? t('catalog.deselect') : t('catalog.select')}
          aria-label={selected ? t('catalog.deselect') : t('catalog.select')}
          aria-pressed={selected}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(video.id);
          }}
        >
          {selected ? '✓' : '+'}
        </button>
      </div>

      <div className={cx(styles.acts, styles.actsRight)} data-show={showActs ? 'true' : 'false'}>
        <div
          className={styles.flyWrap}
          onMouseEnter={(e) => onFlyEnter(video.id, e.currentTarget.getBoundingClientRect())}
          onMouseLeave={() => onFlyLeave(video.id)}
        >
          <button
            type="button"
            className={styles.canvasChip}
            title={t('catalog.addToCanvas')}
            aria-label={t('catalog.addToCanvas')}
            aria-haspopup="menu"
            aria-expanded={!!fly}
            onClick={flyToggle}
          >
            <span className={styles.dashedSquare} aria-hidden="true" />
            {t('catalog.canvasChip')}
          </button>
          {fly ? (
            <CanvasFlyout
              dir={fly.dir}
              canvases={canvases}
              presentIds={video.saved_canvas_ids}
              onPick={(canvasId, already) => onFlyPick(video, canvasId, already)}
              onNew={() => onFlyNew(video)}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
});
